import fetch from './lib/fetch'

export type AddressField = keyof Customer['include']['addresses'][number]

export interface Address {
  id: number
  customer_id: number
  address1: string
  address2: string | null
  city: string
  company: string | null
  country_code: string | null
  created_at: string
  discounts: Discount[]
  first_name: string
  is_gift: boolean
  last_name: string
  order_attributes?: Record<string, unknown>[]
  order_note: string
  phone: string
  presentment_currency: string
  province: string | null
  shipping_lines_conserved: ShippingLine[]
  shipping_lines_override: ShippingLine[]
  updated_at: string
  zip: string
  payment_method_id: number
}

export interface ShippingLine {
  code: string
  price: string
  tax_lines: never[]
  title: string
}

export interface PaymentMethod {
  id: number
  customer_id: number
  billing_address: {
    address1: string
    address2: string | null
    city: string
    company: string | null
    country: string
    country_code: string | null
    first_name: string
    last_name: string
    phone: string
    province: string | null
    zip: string
  }
  created_at: string
  default: boolean
  payment_details: {
    cardholder_name?: string
    inactive?: boolean
    brand: string
    exp_month: number
    exp_year: number
    last4: string
  }
  payment_type: string
  processor_name: 'shopify_payments' | 'stripe' | string
  processor_customer_token: string
  processor_payment_method_token: string
  status: unknown
  status_reason: unknown
  updated_at: string
}

export interface Customer {
  id: number
  analytics_data: {
    utm_params: {
      utm_source: string
      utm_medium: string
      utm_campaign: string
    }[]
  }
  apply_credit_to_next_checkout_charge: boolean
  apply_credit_to_next_recurring_charge: boolean
  backup_payment_methods_enabled: boolean | null
  created_at: string
  email: string
  external_customer_id: {
    ecommerce: string
  }
  first_charge_processed_at: string
  first_name: string
  phone: string
  subscription_related_charge_streak: number
  tax_exempt: boolean
  has_payment_method_in_dunning: boolean
  has_valid_payment_method: boolean
  hash: string
  last_name: string
  subscriptions_active_count: number
  subscriptions_total_count: number
  updated_at: string
  include: {
    addresses: Address[]
    payment_methods: PaymentMethod[]
  }
}

export interface Discount {
  id: number
}

export async function getCustomer(rechargeId: string) {
  return await fetch(
    `${
      import.meta.env.VITE_API_URL
    }/protected/subscriptions/${rechargeId}/customer`,
  )
}

export async function saveCustomerDetails(
  rechargeId: string,
  details: {
    first_name: string
    last_name: string
  },
): Promise<{
  customer: Customer
}> {
  return await fetch(
    `${
      import.meta.env.VITE_API_URL
    }/protected/subscriptions/${rechargeId}/customer/details`,
    {
      method: 'PUT',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify(details),
    },
  )
}

export async function saveCustomerAddress(
  rechargeId: string,
  addressId: number,
  address: Record<AddressField, string>,
): Promise<{
  customer: Customer
}> {
  return await fetch(
    `${
      import.meta.env.VITE_API_URL
    }/protected/subscriptions/${rechargeId}/address/${addressId}`,
    {
      method: 'PUT',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify(address),
    },
  )
}
